import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { useForm, type UseFormReturn } from "react-hook-form";

import { ThreadVisibility, type AgentRecord } from "@/shared/domain/agents";

import {
  makeLaunchFormSchema,
  type LaunchFormOutput,
  type LaunchFormValues,
} from "./launchFormSchema";
import { seedLaunchValues } from "./seedLaunchValues";

export interface UseLaunchFormOptions {
  agent: AgentRecord;
  /** The dataset id the URL carried, or `""` for none. */
  datasetIdFromUrl: string;
}

/**
 * react-hook-form bound to the launch schema for `agent`. The opening values
 * are read once as `defaultValues`, so call it only once the agent has loaded.
 */
export function useLaunchForm({
  agent,
  datasetIdFromUrl,
}: UseLaunchFormOptions): UseFormReturn<
  LaunchFormValues,
  unknown,
  LaunchFormOutput
> {
  const resolver = React.useMemo(
    () => zodResolver(makeLaunchFormSchema(agent.variables)),
    [agent.variables],
  );
  const form = useForm<LaunchFormValues, unknown, LaunchFormOutput>({
    resolver,
    defaultValues: {
      values: seedLaunchValues(agent.variables, datasetIdFromUrl),
      visibility: ThreadVisibility.ORG,
      window: { mode: "datetime", start: "", end: "" },
    },
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const { subscribe, trigger } = form;

  React.useEffect(
    () =>
      // After the first submit, any change re-validates the whole form. With a
      // resolver, react-hook-form refreshes only the changed field's error, but
      // the values are written as one record and the window's error sits on
      // its end, so a fixed row or a fixed start would stay red.
      subscribe({
        formState: { values: true, isSubmitted: true },
        callback: ({ name, isSubmitted }) => {
          if (isSubmitted === true && name !== undefined) {
            void trigger();
          }
        },
      }),
    [subscribe, trigger],
  );

  return form;
}
